import type { AxiosInstance } from 'axios'
import type { ElearningParticipantBulkAction, IFactoryFunctionRequest } from '@bc-library/interface'
import { EMethod } from '@bc-library/interface'
import useAxios from '@/services/api/useAxios'
import { API_MAIN_VERSION, BASE_URL } from '@/config/constantes'
import { useUserStore } from '@/config/store/userStore'

const resource = 'elearningAssignments'

const filter = (axios: IFactoryFunctionRequest<any, any>) => (query: Record<string, any>) => {
  return axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/filter`,
    query,
  })
}

const stats = (axios: IFactoryFunctionRequest<any, any>) => (query: Record<string, any> = {}) => {
  return axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/stats`,
    query,
  })
}

const getOne = (axios: IFactoryFunctionRequest<any, any>) => (assignmentId: string) => {
  return axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/${assignmentId}`,
  })
}

const createOne = (axios: IFactoryFunctionRequest<any, any>) => (data: Record<string, any>) => {
  return axios({
    method: EMethod.post,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2`,
    data,
  })
}

const patchOne = (axios: IFactoryFunctionRequest<any, any>) => (assignmentId: string, data: Record<string, any>) => {
  return axios({
    method: EMethod.patch,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/${assignmentId}`,
    data,
  })
}

/** Learners of a course and where each one stands (managers only). */
const participants = (axios: IFactoryFunctionRequest<any, any>) => (query: Record<string, any>) => {
  return axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/participants`,
    query,
  })
}

const bulkCreate = (axios: IFactoryFunctionRequest<any, any>) => (data: { courseId: string, resourceIds: string[], dueDate?: Date | string | null, language?: string | null }) => {
  return axios({
    method: EMethod.post,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/bulkCreate`,
    data,
  })
}

/** Removes learners from a course, starts their progress over, or changes their due date. */
const bulk = (axios: IFactoryFunctionRequest<any, any>) => (data: { courseId: string, action: ElearningParticipantBulkAction, assignmentIds: string[], dueDate?: Date | string | null }) => {
  return axios({
    method: EMethod.post,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/bulk`,
    data,
  })
}

// Axios requests are cancelled when the tab closes: a keepalive fetch still reaches the API.
// Browsers cap keepalive bodies at 64 KB, so only send small progress payloads with it.
const patchOneOnPageLeave = () => (assignmentId: string, data: Record<string, any>) => {
  const token = useUserStore()?.token
  if (!token)
    return Promise.resolve(null)

  return fetch(`${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/${assignmentId}`, {
    method: 'PATCH',
    keepalive: true,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(data),
  })
}

/** The learner follows the course in another of its languages, keeping their progress. */
const changeLanguage = (axios: IFactoryFunctionRequest<any, any>) => (assignmentId: string, language: string) => {
  return axios({
    method: EMethod.post,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/${assignmentId}/language`,
    data: { language },
  })
}

export default (axios: AxiosInstance) => ({
  filter: filter(useAxios(axios)),
  stats: stats(useAxios(axios)),
  getOne: getOne(useAxios(axios)),
  createOne: createOne(useAxios(axios)),
  patchOne: patchOne(useAxios(axios)),
  participants: participants(useAxios(axios)),
  bulkCreate: bulkCreate(useAxios(axios)),
  bulk: bulk(useAxios(axios)),
  changeLanguage: changeLanguage(useAxios(axios)),
  patchOneOnPageLeave: patchOneOnPageLeave(),
})
